<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import Box from '@uiKit/elements/Box.vue'
import AppImage from '@uiKit/elements/Image.vue'

// Swipeable, auto-advancing photo strip for the property card header —
// mirrors the React Native card. Native scroll-snap does the swiping; a timer
// advances every few seconds and backs off while the user is interacting.
// A tap (pointer moved < 8px) emits `open`; a swipe never does.

interface Props {
  images: string[]
  alt?: string
}

const props = withDefaults(defineProps<Props>(), { alt: '' })

const emit = defineEmits<{ (e: 'open'): void }>()

const AUTO_ADVANCE_MS = 3500
const RESUME_AFTER_INTERACTION_MS = 8000
const SCROLL_SETTLE_MS = 120
const TAP_SLOP_PX = 8

// Raw <div ref> — needs the DOM node for scrollTo / scrollLeft and the
// ResizeObserver (architecture §19 exception).
const trackRef = ref<HTMLElement | null>(null)
const width = ref(0)

// The timer walks a plain index (no re-render per tick of the loop logic);
// `activeIndex` mirrors it only to decide which pages load an <img>.
let index = 0
const activeIndex = ref(0)
const setIndex = (next: number) => {
  index = next
  activeIndex.value = next
}

const count = computed(() => props.images.length)

// Only the current, previous and next (wrapping) pages load an image.
const shouldLoad = (page: number): boolean =>
  page === activeIndex.value ||
  page === activeIndex.value - 1 ||
  page === (activeIndex.value + 1) % count.value

let advanceTimer: ReturnType<typeof setInterval> | undefined
let resumeTimer: ReturnType<typeof setTimeout> | undefined
let settleTimer: ReturnType<typeof setTimeout> | undefined
let resizeObserver: ResizeObserver | null = null
let isPaused = false

const stopAdvance = () => {
  if (advanceTimer !== undefined) clearInterval(advanceTimer)
  advanceTimer = undefined
}

const advance = () => {
  const next = (index + 1) % count.value
  setIndex(next)
  trackRef.value?.scrollTo({ left: next * width.value, behavior: 'smooth' })
}

const startAdvance = () => {
  stopAdvance()
  if (width.value <= 0 || count.value < 2 || isPaused) return
  advanceTimer = setInterval(advance, AUTO_ADVANCE_MS)
}

const clearResume = () => {
  if (resumeTimer !== undefined) clearTimeout(resumeTimer)
  resumeTimer = undefined
}

const pause = () => {
  isPaused = true
  stopAdvance()
  clearResume()
}

const scheduleResume = () => {
  clearResume()
  resumeTimer = setTimeout(() => {
    isPaused = false
    startAdvance()
  }, RESUME_AFTER_INTERACTION_MS)
}

// ── Tap vs swipe ──────────────────────────────────────────────────────
let pointerStart: { x: number; y: number } | null = null
let hasMoved = false

const handlePointerDown = (event: PointerEvent) => {
  pause()
  pointerStart = { x: event.clientX, y: event.clientY }
  hasMoved = false
}

const handlePointerMove = (event: PointerEvent) => {
  if (!pointerStart || hasMoved) return
  const distance = Math.hypot(event.clientX - pointerStart.x, event.clientY - pointerStart.y)
  if (distance >= TAP_SLOP_PX) hasMoved = true
}

const handlePointerUp = () => {
  if (pointerStart && !hasMoved) emit('open')
  pointerStart = null
  scheduleResume()
}

// The browser cancels the pointer when it takes over a swipe — never a tap.
const handlePointerCancel = () => {
  pointerStart = null
  hasMoved = true
  scheduleResume()
}

// After a manual swipe settles, adopt the page the user landed on.
const handleScroll = () => {
  if (settleTimer !== undefined) clearTimeout(settleTimer)
  settleTimer = setTimeout(() => {
    const track = trackRef.value
    if (!track || width.value <= 0) return
    setIndex(Math.min(count.value - 1, Math.round(track.scrollLeft / width.value)))
  }, SCROLL_SETTLE_MS)
}

onMounted(() => {
  const track = trackRef.value
  if (!track || typeof ResizeObserver === 'undefined') return
  resizeObserver = new ResizeObserver((entries) => {
    width.value = entries[0]?.contentRect.width ?? track.clientWidth
  })
  resizeObserver.observe(track)
})

watch([width, count], () => {
  if (index >= count.value) setIndex(0)
  startAdvance()
})

onBeforeUnmount(() => {
  stopAdvance()
  clearResume()
  if (settleTimer !== undefined) clearTimeout(settleTimer)
  resizeObserver?.disconnect()
  resizeObserver = null
})
</script>

<template>
  <div
    ref="trackRef"
    class="card-carousel"
    role="region"
    aria-roledescription="carousel"
    :aria-label="alt || 'Property photos'"
    @pointerdown="handlePointerDown"
    @pointermove="handlePointerMove"
    @pointerup="handlePointerUp"
    @pointercancel="handlePointerCancel"
    @mouseenter="pause"
    @mouseleave="scheduleResume"
    @wheel.passive="pause"
    @scroll.passive="handleScroll"
    @click.stop
  >
    <Box
      v-for="(src, page) in images"
      :key="src"
      class="card-carousel__page"
      :aria-hidden="page !== activeIndex"
    >
      <AppImage
        v-if="shouldLoad(page)"
        :src="src"
        :alt="page === activeIndex ? alt : ''"
        image-class="card-carousel__img"
        draggable="false"
      />
    </Box>
  </div>
</template>

<style scoped>
.card-carousel {
  display: flex;
  width: 100%;
  height: 100%;
  overflow-x: auto;
  overflow-y: hidden;
  scroll-snap-type: x mandatory;
  scrollbar-width: none;
  overscroll-behavior-x: contain;
  touch-action: pan-x pan-y;
  cursor: pointer;
}

.card-carousel::-webkit-scrollbar {
  display: none;
}

.card-carousel__page {
  flex: 0 0 100%;
  height: 100%;
  scroll-snap-align: start;
  background: var(--p-surface-100);
}

.card-carousel__page :deep(.card-carousel__img) {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
  user-select: none;
  -webkit-user-drag: none;
}
</style>
